import { PopoverProps as BasePopoverProps } from '@mui/material';
import { Popover as BasePopover } from '@mui/material';
import * as React from 'react';
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import { useTooltipContainer } from '../../../shared/providers/TooltipContainerProvider';
import { getDataProps, type WithDataProps } from "../../../shared/utilities";

type PopoverProps = WithDataProps<BasePopoverProps> & {
    trigger: React.ReactElement;
    content: React.ReactElement;
}

function Popover(props: PopoverProps) {
    const { trigger: Trigger, content, ...rest } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const tooltipContainerRef = useTooltipContainer();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        event.preventDefault();
        event.stopPropagation();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    if (!Trigger) {
        return "The 'trigger' prop is required.";
    }

    return (
        <>
            {React.cloneElement(Trigger, { onClick: handleClick, "data-open": open })}
            <BasePopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                container={tooltipContainerRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                {...rest}
                {...getDataProps(props)}
            >
                {content}
            </BasePopover>
        </>
    );
}

(Popover as DocumentedComponent).metadata = {
    description: "The `Popover` component allows for displaying an inline modal dialog that is positioned relative to another element on the page.  The dialog becomes visible when the user clicks on the `toggle` element and is hidden when the user clicks away.",
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "trigger", type: "object", exactType: "React.ReactNode", description: "The element to use as the toggle for the popover.  Clicking on this element will toggle the visibility of the modal dialog.  Note that any existing `onClick` event handler for this component will be overridden." },
        { name: "content", type: "object", exactType: "React.ReactNode", description: "The content to render inside the modal dialog.  Any JSX content is supported here." },
        { name: "anchorOrigin", type: "object", exactType: "{ vertical: 'top' | 'center' | 'bottom', horizontal: 'left' | 'center' | 'right' }", template: `anchorOrigin={{\n\tvertical: "bottom",\n\thorizontal: "left"\n}}`, description: "The position to display the modal dialog relative to the trigger." },
        { name: "transformOrigin", type: "object", exactType: "{ vertical: 'top' | 'center' | 'bottom', horizontal: 'left' | 'center' | 'right' }", template: `transformOrigin={{\n\tvertical: "top",\n\thorizontal: "left"\n}}`, description: "The point on the modal which will attach to the trigger." }
    ],
    isSelfClosing: true,
};

export { Popover };